// src/components/RequireAdmin.js
// Route guard for admin-only pages (Settings → Users / Email Templates / API
// Keys / Security / SSO / Audit Log). Requires `role === 'admin'` in the
// custom claims. Mirrors `RequireSystemAdmin` — same self-heal pattern, same
// loading-state shape — but checks the per-subscriber role rather than the
// system_admin flag.
//
// If the role claim is missing (race-immediately-after-login or stale token
// after a subscriber switch), attempts a one-shot self-heal by re-calling
// /auth/set-custom-claims with the current subscriberId. The web's claims-
// race fix in #405 awaits set-custom-claims at login time, so the missing-
// claim path here is now the rare case rather than the common one — but
// the self-heal stays as a safety net.
//
// Denied users redirect by role:
//   - role = 'customer'  → /customer-portal (they shouldn't be on /settings/* anyway)
//   - everything else    → /tasks (manager / user / no role at all)
// No auth → /login.

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../apiConfig';
import { decodeJwtPayload } from '../utils/jwtPayload';

function decodeRoleClaim(token) {
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token) || {};
    return payload.role || null;
  } catch {
    return null;
  }
}

function decodeSubscriberClaim(token) {
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token) || {};
    return payload.subscriber_id || null;
  } catch {
    return null;
  }
}

function deniedRedirectPath(role) {
  if (role === 'customer') return '/customer-portal';
  return '/tasks';
}

export default function RequireAdmin({ children }) {
  const { currentUser, loading, getIdToken } = useAuth();
  const [status, setStatus] = useState('checking'); // 'checking' | 'allowed' | 'denied'
  const [deniedRole, setDeniedRole] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!currentUser) {
      setStatus('denied');
      return;
    }

    let cancelled = false;

    (async () => {
      // Fast path: cached token already says admin.
      let token = await getIdToken(false);
      let role = decodeRoleClaim(token);
      if (role === 'admin') {
        if (!cancelled) setStatus('allowed');
        return;
      }

      // Self-heal: if the role claim is missing entirely, ask the API to
      // (re-)mint subscriber claims. This handles tokens that were minted
      // before #405 awaited set-custom-claims, and tokens stale after a
      // subscriber switch in another tab.
      const subscriberId = decodeSubscriberClaim(token)
        || localStorage.getItem('subscriberId');
      if (!role && subscriberId) {
        try {
          const resp = await fetch(`${API_BASE_URL}/api/v1/auth/set-custom-claims`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ subscriber_id: subscriberId }),
          });
          if (resp.ok) {
            const refreshed = await getIdToken(true);
            role = decodeRoleClaim(refreshed);
            if (role === 'admin') {
              if (!cancelled) setStatus('allowed');
              return;
            }
          }
        } catch (e) {
          console.warn('RequireAdmin self-heal failed:', e);
        }
      }

      if (!cancelled) {
        setDeniedRole(role);
        setStatus('denied');
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser, loading, getIdToken]);

  if (loading || status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'denied') {
    return <Navigate to={currentUser ? deniedRedirectPath(deniedRole) : '/login'} replace />;
  }

  return <>{children}</>;
}
